import { Download, TriangleAlert } from 'lucide-react';

import { DonutChart } from '@/components/charts/donut-chart';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SectionCard } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { exportarCsv } from '@/lib/export';
import { formatDateTimeValue, formatNumber, toNumber } from '@/lib/utils';

import type { OutboxAutoservicio, OutboxFila } from '../autoservicio.types';
import {
  ESTADOS_OUTBOX,
  mensajeAutoservicio,
  reintentosDe,
  varianteOutbox,
} from '../autoservicio-format';

interface PanelColaProps {
  datos: OutboxAutoservicio | undefined;
  cargando: boolean;
  error: unknown;
}

/** Exporta una lista de la cola a CSV. */
function exportarCola(nombre: string, filas: OutboxFila[], columnaFecha: 'proximoReintentoAt' | 'updatedAt') {
  exportarCsv(
    nombre,
    ['Cola', 'Solicitud', 'Reintentos', 'Último intento', columnaFecha === 'updatedAt' ? 'Actualizado' : 'Próximo reintento'],
    filas.map((fila) => [
      fila.id,
      fila.solicitudAbastecimientoId,
      reintentosDe(fila),
      formatDateTimeValue(fila.ultimoIntentoAt),
      formatDateTimeValue(fila[columnaFecha]),
    ]),
  );
}

/** Tabla de la cola (pendientes o fallidos comparten columnas). */
function TablaOutbox({
  filas,
  columnaFecha,
  tituloFecha,
  vacio,
}: {
  filas: OutboxFila[];
  columnaFecha: 'proximoReintentoAt' | 'updatedAt';
  tituloFecha: string;
  vacio: string;
}) {
  if (filas.length === 0) {
    return <p className="py-6 text-center text-sm text-ink-500">{vacio}</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full table-fixed text-sm">
        <thead>
          <tr className="border-b border-ink-100 text-left text-[11px] font-semibold uppercase tracking-wider text-ink-400">
            <th className="w-[14%] pb-2 pr-2">Cola</th>
            <th className="w-[20%] pb-2 pr-2">Solicitud</th>
            <th className="w-[14%] pb-2 pr-2 text-right">Reintentos</th>
            <th className="w-[26%] pb-2 pr-2 text-right">Último intento</th>
            <th className="w-[26%] pb-2 text-right">{tituloFecha}</th>
          </tr>
        </thead>

        <tbody>
          {filas.map((fila) => (
            <tr key={fila.id} className="border-b border-ink-50 last:border-0">
              <td className="py-3 pr-2 font-mono text-sm font-semibold text-ink-900 tabular-nums">
                #{fila.id}
              </td>
              <td className="py-3 pr-2 font-mono text-sm text-ink-700 tabular-nums">
                {formatNumber(fila.solicitudAbastecimientoId)}
              </td>
              <td className="py-3 pr-2 text-right">
                <Badge variant={reintentosDe(fila) > 0 ? 'warning' : 'outline'}>
                  {formatNumber(reintentosDe(fila))}
                </Badge>
              </td>
              <td className="py-3 pr-2 text-right text-[12px] text-ink-600">
                {formatDateTimeValue(fila.ultimoIntentoAt)}
              </td>
              <td className="py-3 text-right text-[12px] text-ink-600">
                {formatDateTimeValue(fila[columnaFecha])}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Pantalla **Cola de envío**: salud del outbox de documentación.
 *
 * Los `pending` con la ventana de reintento vencida y los `failed` que agotaron
 * los reintentos se listan aparte (máx. 20 filas cada uno) porque son los que
 * requieren acción. `completed` y `skipped` no son errores.
 */
export function PanelCola({ datos, cargando, error }: PanelColaProps) {
  if (error) {
    return (
      <Alert variant="error" title="No se pudo cargar la cola de envío">
        {mensajeAutoservicio(error)}
      </Alert>
    );
  }

  const porEstado = datos?.porEstado;
  const pendientes = datos?.pendientes ?? [];
  const fallidos = datos?.fallidos ?? [];
  const total = ESTADOS_OUTBOX.reduce((suma, estado) => suma + toNumber(porEstado?.[estado.clave]), 0);
  const enProceso = toNumber(porEstado?.processing);

  return (
    <div className="space-y-4">
      <div className="grid gap-3 grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
        {ESTADOS_OUTBOX.map((estado) => (
          <div
            key={estado.clave}
            className="rounded-card border border-ink-100 bg-white px-4 py-3 shadow-soft"
          >
            <Badge variant={varianteOutbox(estado.clave)}>{estado.etiqueta}</Badge>
            <p className="mt-2 font-mono text-lg font-bold text-ink-900 tabular-nums">
              {formatNumber(porEstado?.[estado.clave])}
            </p>
          </div>
        ))}
      </div>

      <div className="grid items-stretch gap-4 xl:grid-cols-3">
        <SectionCard title="Distribución de la cola" description="Todos los envíos del período">
          <DonutChart
            labels={ESTADOS_OUTBOX.map((estado) => estado.etiqueta)}
            series={[
              {
                label: 'Envíos',
                data: ESTADOS_OUTBOX.map((estado) => toNumber(porEstado?.[estado.clave])),
              },
            ]}
            format="number"
          />
        </SectionCard>

        <SectionCard title="Reintentos" description="Intentos acumulados del período">
          <div className="flex flex-wrap items-end gap-8">
            <div>
              <p className="text-xs uppercase tracking-wider text-ink-400">Total</p>
              <p className="font-mono text-xl font-bold text-ink-900 tabular-nums">
                {formatNumber(datos?.reintentos.total)}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-ink-400">Máximo por envío</p>
              <p className="font-mono text-xl font-bold text-ink-900 tabular-nums">
                {formatNumber(datos?.reintentos.maximo)}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-ink-400">Envíos en la cola</p>
              <p className="font-mono text-xl font-bold text-ink-900 tabular-nums">
                {formatNumber(total)}
              </p>
            </div>
          </div>

          <div className="mt-4 space-y-2 border-t border-ink-100 pt-3 text-sm">
            <div className="flex items-center justify-between gap-2">
              <span className="text-ink-500">Pendientes atrasados</span>
              <span className="font-mono font-semibold text-ink-900 tabular-nums">
                {formatNumber(pendientes.length)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-ink-500">Fallidos que agotaron reintentos</span>
              <span className="font-mono font-semibold text-ink-900 tabular-nums">
                {formatNumber(fallidos.length)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-ink-500">En proceso</span>
              <span className="font-mono font-semibold text-ink-900 tabular-nums">
                {formatNumber(enProceso)}
              </span>
            </div>
          </div>

          <p className="mt-3 text-[11px] text-ink-400">
            Completados y omitidos no son errores: son envíos resueltos.
          </p>
        </SectionCard>

        <SectionCard
          title="Atención requerida"
          description="Lo que conviene revisar primero"
          action={<TriangleAlert className="size-4 shrink-0 text-ink-400" aria-hidden="true" />}
        >
          <div className="space-y-3">
            <div className="rounded-control border border-rose-200 bg-rose-50 px-3.5 py-3">
              <p className="text-sm font-semibold text-rose-800">
                {formatNumber(fallidos.length)} envío(s) fallido(s)
              </p>
              <p className="mt-0.5 text-[12px] text-rose-700">
                Agotaron los reintentos automáticos; hay que revisar el comprobante en el sistema fiscal.
              </p>
            </div>

            <div className="rounded-control border border-amber-200 bg-amber-50 px-3.5 py-3">
              <p className="text-sm font-semibold text-amber-900">
                {formatNumber(pendientes.length)} pendiente(s) atrasado(s)
              </p>
              <p className="mt-0.5 text-[12px] text-amber-800">
                Su ventana de reintento ya venció y todavía no se enviaron.
              </p>
            </div>

            {total === 0 ? (
              <div className="rounded-control border border-emerald-200 bg-emerald-50 px-3.5 py-3">
                <p className="text-sm font-semibold text-emerald-800">Cola vacía</p>
                <p className="mt-0.5 text-[12px] text-emerald-700">
                  No hay envíos de documentación registrados en el período.
                </p>
              </div>
            ) : null}
          </div>
        </SectionCard>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <SectionCard
          title="Pendientes por reintentar"
          description="Ventana de reintento ya vencida (máx. 20)"
          action={
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportarCola('autoservicio-cola-pendientes', pendientes, 'proximoReintentoAt')}
              disabled={pendientes.length === 0}
              leftIcon={<Download className="size-3.5" aria-hidden="true" />}
            >
              Exportar
            </Button>
          }
        >
          {cargando && !datos ? (
            <Skeleton className="h-24 w-full" />
          ) : (
            <TablaOutbox
              filas={pendientes}
              columnaFecha="proximoReintentoAt"
              tituloFecha="Próximo reintento"
              vacio="No hay envíos pendientes atrasados."
            />
          )}
        </SectionCard>

        <SectionCard
          title="Fallidos"
          description="Agotaron los reintentos y requieren revisión (máx. 20)"
          action={
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportarCola('autoservicio-cola-fallidos', fallidos, 'updatedAt')}
              disabled={fallidos.length === 0}
              leftIcon={<Download className="size-3.5" aria-hidden="true" />}
            >
              Exportar
            </Button>
          }
        >
          {cargando && !datos ? (
            <Skeleton className="h-24 w-full" />
          ) : (
            <TablaOutbox
              filas={fallidos}
              columnaFecha="updatedAt"
              tituloFecha="Actualizado"
              vacio="No hay envíos fallidos."
            />
          )}
        </SectionCard>
      </div>
    </div>
  );
}
